
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { CLOSED_JOB_STATUSES_FILTER } from "@/utils/jobs/status";

// Updated Job interface to match the one in CompanyOffersTab
export interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  description?: string | null;
  candidateCount?: number;
  contact_id?: string | null;
  created_at: string;
  skills?: string[];
  status?: string;
}

export const useCompanyJobsList = (companyId: string | undefined) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  
  const fetchJobs = useCallback(async () => {
    if (!companyId) return;
    
    try {
      setJobsLoading(true);
      
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, description, created_at, company_id, skills, contact_id, companies(name), status')
        .eq('company_id', companyId)
        .not('status', 'in', CLOSED_JOB_STATUSES_FILTER);
      
      if (error) throw error;
      
      if (data) {
        // Fetch all match counts in a single query instead of one per job
        const jobIds = data.map(j => j.id);
        const { data: matchRows } = await supabase
          .from('matches')
          .select('job_id')
          .in('job_id', jobIds);

        const countMap: Record<string, number> = {};
        for (const row of matchRows || [])
          countMap[row.job_id] = (countMap[row.job_id] || 0) + 1;

        const jobsWithCounts = data.map((job) => ({
          ...job,
          candidateCount: countMap[job.id] || 0,
          company_name: (job.companies as any)?.name || 'Unknown',
          skills: job.skills || []
        }));
        
        setJobs(jobsWithCounts);
      }
    } catch (error) {
      console.error("Error fetching company jobs:", error);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [companyId]);
  
  useEffect(() => {
    fetchJobs();
  }, [companyId, fetchJobs]);
  
  // Filter jobs by search term
  const filteredJobs = jobs.filter(job => {
    if (!searchTerm) return true;
    
    // Use universal search for jobs - combine title and description
    const searchableText = [
      job.title,
      job.description || '',
      ...(job.skills || [])
    ].join(' ');
    
    const searchWords = searchTerm.toLowerCase().trim().split(/\s+/);
    return searchWords.every(word => 
      searchableText.toLowerCase().includes(word)
    );
  });
  
  return {
    jobs: filteredJobs,
    jobsLoading,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    refreshJobs: fetchJobs
  };
};
